// 무대 지식. 선배의 젠스파크 에이전트 두 개("무대 구조 설계 디렉터", "3D 비주얼 디렉터") 지침에서 옮겼다.
// 화면에는 ko 를, 그림 모델에는 en 을 보낸다. 지침 원문:
// THE NEXT STUDIO\결과물\2026-10-02 3d 모델링 아트워크 에이전트 제작\젠스파크_*_원문지시문.md

export type Option = { id: string; ko: string; hint?: string; en: string };

/** 요소의 종류. 공간 읽기(AI)도 이 id 중에서 고른다 */
export const ELEMENT_KINDS: Option[] = [
  { id: "main-stage", ko: "메인 무대", hint: "공연이 이루어지는 주 무대", en: "main performance stage" },
  { id: "center-stage", ko: "중앙 원형 무대", hint: "회장 가운데 독립된 무대, 사방에서 보임", en: "central island stage seen from all sides" },
  { id: "thrust", ko: "돌출 무대", hint: "객석 쪽으로 뻗어 나온 무대(T자·원형 끝)", en: "thrust stage extending into the audience" },
  { id: "runway", ko: "런웨이", hint: "무대와 무대를 잇는 좁고 긴 길", en: "narrow runway connecting stages" },
  { id: "ramp-ring", ko: "링·곡선 램프 무대", hint: "곡선으로 휘어 감기는 길 모양 무대", en: "curved ring-shaped ramp stage" },
  { id: "lift", ko: "리프트(승강 장치)", hint: "출연자·장치가 오르내리는 장치", en: "stage lift that rises and lowers" },
  { id: "main-led", ko: "메인 LED", hint: "무대 정중앙 뒤 큰 가로형 화면", en: "large main LED backdrop screen" },
  { id: "pgm-led", ko: "PGM LED(세로형)", hint: "중계·클로즈업이 나오는 세로 화면, 메인 좌우", en: "vertical program LED screens showing live camera close-ups" },
  { id: "imag-led", ko: "IMAG LED", hint: "멀리 있는 관객용 큰 확대 화면", en: "large image-magnification LED screens for distant audiences" },
  { id: "side-led", ko: "사이드 LED", hint: "무대 양옆 세로·사각 화면", en: "side LED screens flanking the stage" },
  { id: "floor-led", ko: "플로어 LED", hint: "무대 바닥에 영상이 나오는 화면", en: "LED video floor on the stage surface" },
  { id: "kinetic-led", ko: "키네틱 LED", hint: "움직이는 LED 큐브·바·패널", en: "kinetic moving LED cubes, bars and panels" },
  { id: "ring-led", ko: "원형·링 LED", hint: "무대 위 가운데 매달린 둥근 화면", en: "circular ring LED suspended above the stage" },
  { id: "cylinder-led", ko: "원통형 LED", hint: "원통 둘레 전체가 화면인 LED", en: "cylindrical LED video screen whose whole outer surface shows video" },
  { id: "custom-led", ko: "이형 LED", hint: "알·날개·기하 형태 같은 특수 모양 화면", en: "custom-shaped LED (egg, wings or geometric form)" },
  { id: "truss", ko: "트러스·리깅", hint: "무대 위 조명·화면을 매다는 철골 구조", en: "overhead truss rigging grid" },
  { id: "tower", ko: "사이드 타워", hint: "무대 좌우 수직 구조물(화면·스피커·조명)", en: "vertical side towers carrying screens, speakers and lights" },
  { id: "roof", ko: "무대 지붕", hint: "야외 무대 위를 덮는 지붕 구조", en: "outdoor stage roof structure" },
  { id: "cauldron", ko: "성화대", hint: "성화가 타는 상징 구조물", en: "ceremonial cauldron where the flame burns" },
  { id: "podium", ko: "단상·연단", hint: "선언·연설·시상용 단", en: "podium or lectern platform for speeches and declarations" },
  { id: "landmark", ko: "상징 조형물", hint: "행사의 상징이 되는 큰 조형물", en: "large symbolic landmark sculpture" },
  { id: "audience", ko: "객석·관중석", hint: "관객이 앉거나 서는 곳", en: "audience seating stands" },
  { id: "field", ko: "그라운드·필드", hint: "경기장 바닥 면", en: "stadium field surface" },
  { id: "booth", ko: "부스·체험존", hint: "전시·체험용 부스", en: "exhibition or experience booth" },
  { id: "gate", ko: "게이트·진입부", hint: "입구 구조물", en: "entrance gate structure" },
  { id: "other", ko: "기타", hint: "위에 없는 것", en: "" },
];

/** 장소 종류. shape 는 3D 공간을 만들 때 쓴다: bowl(관중석이 둘러쌈) · ground(평지) · hall(경사 객석) · room(평평한 홀) */
export type Venue = Option & { group: "sports" | "general"; shape: "bowl" | "ground" | "hall" | "room"; track?: boolean; roof?: boolean; scenery?: "trees" | "water" | "buildings" | "hills"; inner: [number, number]; capacity: number };

export const VENUE_TYPES: Venue[] = [
  { id: "stadium", ko: "종합운동장 (육상 트랙)", hint: "축구장 둘레에 트랙, 2~5만 명, 지붕 없음", en: "open-air athletics stadium with a running track around the field", group: "sports", shape: "bowl", track: true, inner: [176, 112], capacity: 30000 },
  { id: "football", ko: "축구 전용 경기장", hint: "관중석이 경기장에 바짝, 2~6만 명", en: "open-air football stadium with stands close to the pitch", group: "sports", shape: "bowl", inner: [124, 84], capacity: 30000 },
  { id: "dome", ko: "돔 구장", hint: "지붕 있는 대형 경기장, 2~5만 명", en: "covered dome stadium", group: "sports", shape: "bowl", roof: true, inner: [130, 110], capacity: 35000 },
  { id: "arena", ko: "실내 체육관·아레나", hint: "5천~2만 명, 지붕 있음", en: "indoor arena", group: "sports", shape: "bowl", roof: true, inner: [64, 42], capacity: 10000 },
  { id: "outdoor", ko: "야외 특설 부지·공터", hint: "1만~10만 명이 서서 보는 넓은 평지", en: "open-air festival ground with a temporary stage", group: "general", shape: "ground", inner: [150, 130], capacity: 20000 },
  { id: "plaza", ko: "도심 광장", hint: "건물로 둘러싸인 광장, 시민 축제", en: "open-air city plaza surrounded by buildings", group: "general", shape: "ground", scenery: "buildings", inner: [130, 100], capacity: 15000 },
  { id: "park", ko: "공원 잔디광장", hint: "나무로 둘러싸인 잔디밭, 피크닉형 페스티벌", en: "large lawn in a park surrounded by trees", group: "general", shape: "ground", scenery: "trees", inner: [140, 110], capacity: 15000 },
  { id: "waterfront", ko: "해변·수변", hint: "바다·호수를 등진 무대", en: "waterfront festival site with the stage at the water's edge", group: "general", shape: "ground", scenery: "water", inner: [150, 110], capacity: 20000 },
  { id: "riverside", ko: "강변 둔치", hint: "강을 따라 길게 펼쳐진 둔치", en: "long riverside park along a river", group: "general", shape: "ground", scenery: "water", inner: [180, 80], capacity: 20000 },
  { id: "nature", ko: "자연 속 야외무대", hint: "산·숲을 배경으로 한 야외 공연장", en: "outdoor stage in nature with hills and forest behind", group: "general", shape: "ground", scenery: "hills", inner: [130, 100], capacity: 8000 },
  { id: "hall", ko: "공연장·컨벤션홀", hint: "500~3천 명, 경사진 객석이 무대를 정면으로", en: "concert hall or convention hall with raked seating", group: "general", shape: "hall", inner: [44, 50], capacity: 2000 },
  { id: "ballroom", ko: "호텔 볼룸", hint: "200~800명, 원형 테이블", en: "hotel ballroom with round banquet tables", group: "general", shape: "room", inner: [36, 28], capacity: 500 },
  { id: "street", ko: "거리·도심", hint: "거리 연출, 보행 동선", en: "city street", group: "general", shape: "ground", inner: [120, 60], capacity: 5000 },
  { id: "other", ko: "기타", en: "", group: "general", shape: "ground", inner: [120, 100], capacity: 5000 },
];

export const CAPACITIES = [500, 2000, 5000, 10000, 30000, 50000];
export const capacityKo = (n: number) => (n >= 10000 ? `${n / 10000}만 명` : `${n.toLocaleString()}명`);

export const EVENT_KINDS: Option[] = [
  { id: "sports", ko: "체육행사 개·폐회식", en: "the opening or closing ceremony of a sports event" },
  { id: "concert", ko: "공연·콘서트", en: "a concert" },
  { id: "festival", ko: "축제·페스티벌", en: "a festival" },
  { id: "corporate", ko: "기업·의전 행사", en: "a corporate or official ceremony" },
  { id: "exhibition", ko: "전시·박람회", en: "an exhibition" },
  { id: "other", ko: "기타", en: "an event" },
];

/** 그림 느낌. 품질 문장이 여기에 따라 바뀐다 */
export const TONES: Option[] = [
  { id: "photo", ko: "실사 사진", hint: "실제 행사를 찍은 사진처럼", en: "a photorealistic event photograph taken with a professional camera" },
  { id: "render3d", ko: "3D 렌더", hint: "제안서용 3D CG 투시도처럼", en: "a high-end 3D visualization render for a proposal (clean architectural CG, V-Ray or Unreal look), clearly a 3D render rather than a photograph" },
];

export type ElementDef = { kind: string; name: string; what: string; en: string; notEn?: string };

/** 무대 형태 예시. 처음 쓰는 사람이 고르는 카드. 3D 공간도 이것으로 만든다 */
export type StagePreset = Option & { groups: ("sports" | "general")[]; elements: ElementDef[]; spot?: boolean };

export const STAGE_PRESETS: StagePreset[] = [
  {
    id: "spot-end",
    ko: "무대 자리: 한쪽 끝",
    hint: "모양은 정하지 않고 자리만. 무대 디자인은 다음 단계에서 AI가 여러 안으로",
    en: "stage area at one end",
    groups: ["sports", "general"],
    spot: true,
    elements: [{ kind: "main-stage", name: "무대 자리", what: "한쪽 끝 무대가 들어설 자리. 모양은 아직 정하지 않음", en: "The flat outlined area at one end is where the stage stands; the stage design itself is still open." }],
  },
  {
    id: "spot-center",
    ko: "무대 자리: 가운데",
    hint: "가운데 무대(사방에서 봄). 모양은 다음 단계에서",
    en: "stage area at the center",
    groups: ["sports", "general"],
    spot: true,
    elements: [{ kind: "center-stage", name: "무대 자리", what: "가운데 무대가 들어설 자리. 모양은 아직 정하지 않음", en: "The flat outlined area at the center is where the stage stands, seen from all sides; the stage design itself is still open." }],
  },
  {
    id: "end",
    ko: "엔드 스테이지",
    hint: "한쪽 끝 정면 무대. 공연·시상식의 기본형",
    en: "end stage",
    groups: ["sports", "general"],
    elements: [
      { kind: "main-stage", name: "메인 무대", what: "한쪽 끝에 놓인 넓은 정면 무대", en: "A wide end stage stands at one end of the venue, facing the audience." },
      { kind: "main-led", name: "메인 LED", what: "무대 뒤 큰 가로형 LED 화면", en: "The large dark navy panel behind the stage is the main LED video screen." },
      { kind: "pgm-led", name: "세로형 LED 2개", what: "메인 LED 좌우의 세로 화면 두 개", en: "The two tall dark navy panels on the left and right of the main screen are vertical LED video screens." },
    ],
  },
  {
    id: "thrust-t",
    ko: "돌출 T자",
    hint: "무대에서 객석으로 뻗은 길 끝에 T자 무대. K-POP 콘서트형",
    en: "end stage with a T-shaped thrust runway",
    groups: ["sports", "general"],
    elements: [
      { kind: "main-stage", name: "메인 무대", what: "한쪽 끝에 놓인 넓은 정면 무대", en: "A wide end stage stands at one end of the venue, facing the audience." },
      { kind: "main-led", name: "메인 LED", what: "무대 뒤 큰 가로형 LED 화면", en: "The large dark navy panel behind the stage is the main LED video screen." },
      { kind: "thrust", name: "돌출 런웨이(T자)", what: "무대 가운데서 객석 쪽으로 곧게 뻗은 길, 끝이 T자로 넓어짐", en: "A straight runway extends from the center of the stage into the audience and ends in a T-shaped platform.", notEn: "a road or a bridge" },
    ],
  },
  {
    id: "center",
    ko: "중앙 원형 (360도)",
    hint: "가운데 둥근 무대를 관객이 사방에서 봄",
    en: "central round island stage",
    groups: ["sports", "general"],
    elements: [
      { kind: "center-stage", name: "중앙 원형 무대", what: "회장 한가운데 둥근 무대, 사방에서 보임", en: "A round island stage stands at the center of the venue and is seen from all sides." },
      { kind: "ring-led", name: "링 LED", what: "중앙 무대 위에 매달린 둥근 고리 모양 LED 화면", en: "The dark navy ring hanging above the center stage is a circular ring LED video screen." },
    ],
  },
  {
    id: "ring-swirl",
    ko: "링·나선 램프 (U대회형)",
    hint: "중앙 원형 무대를 곡선 램프가 소용돌이처럼 감쌈. 위에 원통형 LED",
    en: "central round stage wrapped by swirl-shaped curved ramps",
    groups: ["sports"],
    elements: [
      { kind: "center-stage", name: "중앙 원형 무대", what: "필드 한가운데 둥근 무대", en: "A flat round stage stands at the center of the field." },
      { kind: "ramp-ring", name: "나선 곡선 램프", what: "중앙 무대를 소용돌이처럼 감싸는 긴 곡선 램프 두 줄기", en: "Two long curved ramp arms wrap around the center stage like a swirl; they are raised performance ramps.", notEn: "an athletic running track" },
      { kind: "cylinder-led", name: "원통형 LED", what: "중앙 무대 위에 매달린 원통, 둘레 전체에 영상이 나옴", en: "The dark navy cylinder hanging above the center stage is a cylindrical LED video screen whose whole outer surface shows video.", notEn: "a truss ring" },
    ],
  },
  {
    id: "runway-sub",
    ko: "런웨이 + 서브 무대",
    hint: "메인 무대에서 긴 길로 객석 가운데 작은 둥근 무대까지",
    en: "end stage with a long runway to a round sub stage",
    groups: ["sports", "general"],
    elements: [
      { kind: "main-stage", name: "메인 무대", what: "한쪽 끝에 놓인 넓은 정면 무대", en: "A wide end stage stands at one end of the venue, facing the audience." },
      { kind: "main-led", name: "메인 LED", what: "무대 뒤 큰 가로형 LED 화면", en: "The large dark navy panel behind the stage is the main LED video screen." },
      { kind: "runway", name: "런웨이", what: "메인 무대에서 객석 가운데로 이어지는 긴 길", en: "A long narrow runway connects the main stage to a small round sub stage in the middle of the audience." },
    ],
  },
  {
    id: "festival-roof",
    ko: "페스티벌 지붕 무대",
    hint: "지붕 트러스를 얹은 야외 무대, 좌우 대형 화면 타워",
    en: "festival main stage with a roof truss",
    groups: ["general", "sports"],
    elements: [
      { kind: "main-stage", name: "메인 무대", what: "지붕 트러스를 얹은 넓은 야외 무대", en: "A wide festival stage stands at one end under a flat roof truss supported by four towers." },
      { kind: "main-led", name: "메인 LED", what: "무대 뒤 큰 가로형 LED 화면", en: "The large dark navy panel at the back of the stage is the main LED video screen." },
      { kind: "imag-led", name: "좌우 IMAG 화면", what: "무대 양옆 타워에 달린 큰 화면 두 개", en: "The two dark navy panels on towers to the left and right of the stage are large IMAG LED screens." },
    ],
  },
  {
    id: "cross",
    ko: "십자형 4방향",
    hint: "가운데 무대에서 네 방향으로 길이 뻗음",
    en: "center stage with four runways in a cross",
    groups: ["sports", "general"],
    elements: [
      { kind: "center-stage", name: "중앙 무대", what: "회장 가운데 네모난 무대", en: "A square stage stands at the center of the venue." },
      { kind: "runway", name: "4방향 런웨이", what: "가운데 무대에서 네 방향으로 뻗은 길", en: "Four narrow runways extend from the center stage in four directions, forming a cross." },
    ],
  },
  {
    id: "field-show",
    ko: "필드 퍼포먼스 (개·폐회식형)",
    hint: "필드 전체가 공연 공간, 한쪽에 긴 의전 무대와 화면",
    en: "field-wide ceremony performance area with a long protocol stage",
    groups: ["sports"],
    elements: [
      { kind: "main-stage", name: "의전 무대", what: "필드 한쪽 긴 쪽에 놓인 길고 낮은 의전 무대", en: "A long low protocol stage stands along one long side of the field." },
      { kind: "main-led", name: "메인 LED", what: "의전 무대 뒤 가로로 긴 LED 화면", en: "The long dark navy panel behind the protocol stage is the main LED video screen." },
      { kind: "field", name: "공연 필드", what: "필드 전체가 비어 있는 공연 공간", en: "The whole field is an open performance area." },
    ],
  },
];

/** 확정한 무대를 다른 방향에서 볼 때의 시점 (공간 합의용) */
export const ANGLE_VIEWS: Option[] = [
  { id: "front", ko: "정면", en: "a straight-on frontal view from the middle of the audience, at medium height" },
  { id: "three-quarter", ko: "사선", en: "a three-quarter view from about 40 degrees off the front axis, slightly elevated" },
  { id: "side", ko: "측면", en: "a side view from the left side of the stage" },
  { id: "aerial", ko: "조감", en: "a high aerial bird's-eye view that shows the whole stage layout and the venue around it" },
];

/** 무대 디자인 시안의 품질 문장 (1차) */
export const STAGE_DESIGN_EN = "This is a stage design proposal image: the stage, its structure, screens and lighting must be clearly readable. Stage lighting is on at dusk. Few or no performers. A light audience only where it helps the sense of scale.";

/** 무대 형태에 더할 것 */
export const EXTRAS: (Option & { def: ElementDef })[] = [
  { id: "cauldron", ko: "성화대", en: "cauldron", def: { kind: "cauldron", name: "성화대", what: "관중석 끝 높은 곳의 성화대", en: "A tall ceremonial cauldron tower stands at the far end of the venue." } },
  { id: "podium", ko: "선언대·단상", en: "podium", def: { kind: "podium", name: "선언대", what: "무대 앞 작은 단상(개회 선언·연설용)", en: "A small podium platform stands at the front edge of the performance area." } },
  { id: "cylinder", ko: "원통형 LED", en: "cylinder LED", def: { kind: "cylinder-led", name: "원통형 LED", what: "무대 위에 매달린 원통, 둘레 전체에 영상", en: "The dark navy cylinder hanging above the stage is a cylindrical LED video screen.", notEn: "a truss ring" } },
  { id: "towers", ko: "사이드 화면 타워", en: "side towers", def: { kind: "tower", name: "사이드 타워", what: "무대 좌우 세로 구조물과 큰 화면", en: "Two vertical side towers with dark navy LED screens flank the stage." } },
  { id: "lift", ko: "무대 리프트", en: "lift", def: { kind: "lift", name: "리프트", what: "무대 가운데 둥근 승강 장치", en: "The round disc in the middle of the stage is a lift that rises and lowers." } },
];

/** 고른 장소·무대·더할 것으로 요소 목록을 만든다 (3D 공간을 정할 때 서버가 쓴다) */
export function modelElements(m: { venueType: string; capacity: number; stage: string; extras: string[] }): ElementDef[] {
  const v = VENUE_TYPES.find((x) => x.id === m.venueType) ?? VENUE_TYPES[VENUE_TYPES.length - 1];
  const who = capacityKo(m.capacity);
  const out: ElementDef[] = [];
  if (v.shape === "bowl") {
    out.push({ kind: "audience", name: "관중석", what: `경기장을 둘러싼 계단식 관중석, 약 ${who}`, en: `The light gray tiered stands around the field are audience stands for about ${m.capacity.toLocaleString("en-US")} people.` });
    out.push({ kind: "field", name: "필드", what: v.track ? "가운데 평평한 경기장 바닥, 둘레에 육상 트랙" : "가운데 평평한 경기장 바닥", en: v.track ? "The flat ground inside the stands is the stadium field, surrounded by a running track." : "The flat ground inside the stands is the stadium field." });
    if (v.roof) out.push({ kind: "roof", name: "지붕", what: "실내, 지붕이 덮고 있음", en: "The venue is indoors, under a roof." });
  } else if (v.shape === "ground") {
    out.push({ kind: "audience", name: "관객 구역", what: `무대 앞 평지, 서서 보는 관객 약 ${who}`, en: `The open flat ground in front of the stage is a standing audience area for about ${m.capacity.toLocaleString("en-US")} people.` });
    if (v.scenery === "trees") out.push({ kind: "landmark", name: "나무", what: "잔디광장을 둘러싼 나무들", en: "The green round shapes around the lawn are park trees; the audience area is a grass lawn." });
    if (v.scenery === "water") out.push({ kind: "landmark", name: "물", what: v.id === "riverside" ? "부지 한쪽을 따라 흐르는 강" : "무대 뒤로 펼쳐진 바다·호수", en: v.id === "riverside" ? "The blue flat strip along one side is a river." : "The blue flat area behind the stage is open water (sea or lake)." });
    if (v.scenery === "buildings") out.push({ kind: "landmark", name: "도심 건물", what: "광장을 둘러싼 건물들", en: "The gray blocks around the plaza are city buildings; the ground is a paved plaza." });
    if (v.scenery === "hills") out.push({ kind: "landmark", name: "산과 숲", what: "무대 뒤로 보이는 산과 숲", en: "The large green mounds behind the stage are wooded hills; the setting is in nature." });
  } else if (v.shape === "hall") {
    out.push({ kind: "audience", name: "객석", what: `무대를 정면으로 보는 경사진 객석, 약 ${who}`, en: `The raked seating facing the stage is audience seating for about ${m.capacity.toLocaleString("en-US")} people.` });
  } else {
    out.push({ kind: "audience", name: "원형 테이블", what: `평평한 홀에 원형 테이블, 약 ${who}`, en: `The round tables on the flat floor seat about ${m.capacity.toLocaleString("en-US")} guests.` });
  }
  const preset = STAGE_PRESETS.find((s) => s.id === m.stage);
  if (preset) out.push(...preset.elements);
  for (const id of m.extras) {
    const x = EXTRAS.find((e) => e.id === id);
    if (x && !out.some((o) => o.kind === x.def.kind)) out.push(x.def);
  }
  out.push({ kind: "other", name: "사람 크기 기준", what: "주황색 작은 사람 모형은 키 1.75m인 사람", en: "The small orange figures are people 1.75 m tall; keep exactly that human scale and render them as real people in varied clothing, not orange." });
  return out;
}

/** 장면. 선배의 실제 업무(대형 페스티벌·체육행사 개폐회식) 기준. HANDOFF 4절 */
export const SCENES: Option[] = [
  { id: "opening-main", ko: "개회식 본무대", en: "the opening ceremony on the main stage" },
  { id: "closing-main", ko: "폐회식 본무대", en: "the closing ceremony on the main stage" },
  { id: "flame", ko: "성화·의전", en: "the flame lighting and official protocol moment" },
  { id: "pre-show", ko: "식전 축하공연", en: "the pre-ceremony celebration performance" },
  { id: "performance", ko: "공연 하이라이트", en: "the highlight of a live performance" },
  { id: "storyboard", ko: "연출 스토리보드 한 장면", en: "one storyboard moment of the show" },
  { id: "led-content", ko: "LED 화면 콘텐츠 보여주기", en: "a moment that showcases the LED screen content" },
  { id: "layout", ko: "행사장 배치 조감", en: "an overview of the whole event layout" },
  { id: "sightline", ko: "객석에서 본 시야", en: "the view from an audience seat" },
  { id: "key-visual", ko: "키비주얼", en: "a key visual of the event" },
  { id: "experience", ko: "체험존", en: "visitors enjoying the experience zone" },
  { id: "structure", ko: "구조 확인용 (공간 합의)", hint: "사람·효과 없이 무대 구조만 또렷하게. 공간 그림이 없을 때 먼저 뽑아 합의한다", en: "a clean structural view of the venue for agreeing on the space: daylight, no crowd, no effects, every structure clearly readable" },
];

/** 컷 목적. 3D 비주얼 디렉터 지침 [컷 목적 해석] */
export const PURPOSES: Option[] = [
  { id: "main", ko: "메인컷", hint: "제안서에서 가장 강한 대표 이미지", en: "the strongest hero image of the proposal" },
  { id: "explain", ko: "설명컷", hint: "구조·위치·기능이 읽혀야 함", en: "an explanatory image where structure, position and function are clearly readable" },
  { id: "landmark", ko: "랜드마크컷", hint: "상징물의 존재감", en: "an image that shows the presence and scale of the symbolic object" },
  { id: "action", ko: "액션컷", hint: "사람의 활동·반응 중심", en: "an action image centered on people's activity and reactions" },
  { id: "operation", ko: "운영컷", hint: "동선·체험 흐름", en: "an operational image showing visitor flow and how the space is used" },
  { id: "layout", ko: "레이아웃컷", hint: "전체 구성 관계", en: "a layout image showing how the whole venue is organized" },
];

export const TIMES: Option[] = [
  { id: "night", ko: "밤", en: "at night" },
  { id: "dusk", ko: "해 질 녘", en: "at dusk with a deep blue sky" },
  { id: "day", ko: "낮", en: "in daylight" },
];

/** 카메라 언어. 3D 비주얼 디렉터 지침 [카메라 언어 엔진] */
export const CAMERAS: Option[] = [
  { id: "same", ko: "공간 그림과 같은 시점", hint: "기준 그림의 카메라를 그대로", en: "" },
  { id: "eye", ko: "관객 눈높이", hint: "객석에 앉은 사람의 시야", en: "eye-level view from an audience seat, about 1.6 m high" },
  { id: "high", ko: "위에서 내려다보기", hint: "하이앵글", en: "high-angle view looking down on the stage" },
  { id: "aerial", ko: "드론 조감", hint: "버드아이", en: "aerial bird's-eye drone view of the whole venue" },
  { id: "three-quarter", ko: "사선(3/4) 투시", hint: "더 입체적으로", en: "three-quarter perspective view from 30 to 45 degrees off the front axis" },
  { id: "front", ko: "정면", en: "straight-on frontal view of the stage" },
  { id: "low", ko: "아래에서 올려다보기", hint: "로우앵글", en: "low-angle view looking up at the stage" },
];

export const PEOPLE: Option[] = [
  { id: "full", ko: "관객 가득", en: "the stands are packed with spectators" },
  { id: "athletes", ko: "선수단·참가자 모임", en: "athletes and participants gather on the field" },
  { id: "performers", ko: "공연자 소수", en: "a small group of performers on stage" },
  { id: "speaker", ko: "연설·선언하는 사람", en: "one person speaking at the podium" },
  { id: "none", ko: "사람 없음", en: "no people" },
];

export const EFFECTS: Option[] = [
  { id: "drone", ko: "드론쇼", en: "a drone light show in the sky" },
  { id: "fireworks", ko: "불꽃놀이", en: "fireworks" },
  { id: "laser", ko: "레이저", en: "laser beams" },
  { id: "flame", ko: "불기둥·화염", en: "stage flame jets" },
  { id: "fog", ko: "포그·헤이즈", en: "light haze that catches the light beams" },
  { id: "confetti", ko: "컨페티", en: "confetti" },
  { id: "searchlight", ko: "서치라이트", en: "searchlight beams" },
  { id: "lightstick", ko: "관객 응원봉 빛", en: "spectators holding small lights so the stands glitter" },
];

export const ASPECTS = ["16:9", "21:9", "4:3", "1:1", "9:16"] as const;

/** 고칠 곳의 종류. 보정 1번(U대회)에서 통한 짜임에 맞춘다 */
export const FIX_TYPES: Option[] = [
  { id: "wrong", ko: "모습이 달라요", en: "looks wrong" },
  { id: "remove", ko: "없어야 해요", en: "must not be there" },
  { id: "add", ko: "빠졌어요", en: "is missing" },
  { id: "size", ko: "크기가 이상해요", en: "has the wrong size" },
  { id: "position", ko: "위치가 달라요", en: "is in the wrong position" },
  { id: "light", ko: "빛·색이 아쉬워요", en: "needs different light or color" },
  { id: "people", ko: "사람 수·크기", en: "has the wrong number or scale of people" },
  { id: "text", ko: "글자가 깨졌어요", en: "has broken or invented text" },
];

/** 선배 지침의 렌더 품질 기준과 "절제된 프리미엄" 기본 강도. 모든 생성에 붙는다. 첫 문장은 그림 느낌(TONES)에 따라 바뀐다 */
export function qualityEn(tone: string | undefined): string[] {
  const t = TONES.find((x) => x.id === tone) ?? TONES[0];
  return [
    `Style: ${t.en}. Proposal-grade quality: realistic materials, clean shadows, orderly light sources, convincing scale, nothing overdone.`,
    "Restrained premium finish: soft thin volumetric light, subtle glow that keeps LED content readable, hint-level lens flare, shallow haze and particles.",
    "Do not invent text: no made-up words, letters, numbers or logos anywhere unless they are specified.",
  ];
}

export const MODELS = {
  // 젠스파크 시험(2026-10-13)에서 잘 나온 모델. 2K 한 장 $0.0504 (2026-10-17 공식 가격표)
  fast: { id: "gemini-nano-banana-2.1", ko: "기본 (Nano Banana 2.1)", sizes: { "1K": 0.0336, "2K": 0.0504, "4K": 0.113 } },
  // 글자·로고가 중요한 최종본. 2K $0.134, 4K $0.24
  pro: { id: "gemini-3-pro-image", ko: "정밀 (Nano Banana Pro)", sizes: { "1K": 0.134, "2K": 0.134, "4K": 0.24 } },
} as const;
export type ModelKey = keyof typeof MODELS;
export type SizeKey = "1K" | "2K" | "4K";

/** 크레딧: 기본 모델 2K 한 장 = 1 크레딧 */
export function creditsFor(model: ModelKey, size: SizeKey): number {
  const usd = MODELS[model].sizes[size];
  return Math.max(1, Math.round(usd / MODELS.fast.sizes["2K"]));
}

export const byId = (list: Option[], id: string | undefined) => list.find((o) => o.id === id);
